import { useEffect, useMemo } from 'react';
import { ActionType, ArticlePath, createGuideLink, TextKey } from '@mews-commander/enums';
import { ModalWrapper, NewModalBodyWrapper, OverlayedLoader } from '@mews-commander/ui';
import { LocalizedNumber, LocalizedText, lt, useGainsightService } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { IconName } from '@optimus-web/icons';
import { HeadingAction } from '../../../legacy';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { AccountMergeContentWrapper } from './AccountMergeContentWrapper';
import { AccountMergeEmptyContent } from './AccountMergeEmptyContent';
import { getGainSightEventObject, MatchAndMergeGainSightEvents } from './GainSightEvents';
import { useAccountMergeState, useMatchedProfiles, useNavigateToNextMatch } from './hooks';
import { MergeMode } from './types';

export const AccountMerge = () => {
    const { guestProfileIds, mergeMode, totalProfileMatches, previousMatchId, isQueueEmpty } = useAccountMergeState();
    const gainSightService = useGainsightService();
    const guideLink = createGuideLink(ArticlePath.MergeCustomerProfiles);

    const { profiles: matchedGuestProfiles, loading } = useMatchedProfiles({
        guestProfileIds,
        mergeMode,
        previousMatchId,
    });

    const navigateToNextMatch = useNavigateToNextMatch({
        totalProfileMatches,
        matchedGuestProfiles,
    });

    /** Gainsight event Tracking Start */
    useEffect(() => {
        /** These conditions prevent duplicate calls for gain sight services */
        if (!isQueueEmpty && !loading && matchedGuestProfiles?.profiles?.length) {
            gainSightService.trackEvent(
                MatchAndMergeGainSightEvents.MatchAndMergeDisplayed,
                getGainSightEventObject(matchedGuestProfiles, mergeMode)
            );
        }
    }, [gainSightService, loading, isQueueEmpty, matchedGuestProfiles, mergeMode]);

    /** Gainsight event Tracking End */

    const actions: HeadingAction[] = [
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    const title = useMemo(() => {
        return (
            <Stack spacing="100" itemAlignment="center">
                <Stack>{lt('AccountMergePageTitle')}</Stack>
                {mergeMode === MergeMode.Queue ? (
                    <Typography textStyle="bodyMedium" as="div">
                        <LocalizedText
                            textKey={TextKey.AccountMergeTotalProfilesToReview}
                            substitutes={{ count: <LocalizedNumber number={totalProfileMatches} /> }}
                        />
                    </Typography>
                ) : null}
            </Stack>
        );
    }, [mergeMode, totalProfileMatches]);

    const bodyContent = useMemo(() => {
        if (isQueueEmpty) {
            return <AccountMergeEmptyContent mergeMode={mergeMode} />;
        }

        return (
            <>
                <OverlayedLoader show={loading} />
                {!loading && matchedGuestProfiles ? (
                    <AccountMergeContentWrapper
                        matchedGuestProfiles={matchedGuestProfiles}
                        guideLink={guideLink}
                        mergeMode={mergeMode}
                        navigateToNextMatch={navigateToNextMatch}
                    />
                ) : null}
            </>
        );
    }, [isQueueEmpty, mergeMode, loading, matchedGuestProfiles, guideLink, navigateToNextMatch]);

    return (
        <ModalWrapper>
            <ModalHeader title={title} actions={actions} />
            <NewModalBodyWrapper padded>{bodyContent}</NewModalBodyWrapper>
        </ModalWrapper>
    );
};
